import React, { useState, useEffect, memo } from 'react'
import PropTypes from 'prop-types';
import styles from './comments-section.module.scss';

import { Button } from 'react-bootstrap';
import Spinner from 'react-bootstrap/Spinner';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import CgIcon from '@/components/atoms/cg-icon';

import RadioButtonComponent from '@/components/atoms/radiobutton';
import RightPanel from '@/components/molecules/right-panel';
import Avatar from '@/components/atoms/avatar';


import {useHttpPost, useHttpDelete, useHttpPut, useHttpGet} from '@/hooks/use-http';
import {useObservable, useTranslation} from '@/hooks';
import {
  COMMENTS_CREATE, COMMENTS_EDIT, COMMENTS_LIKE,
  COMMENTS_FLAG, COMMENTS_DELETE, COMMENTS_UNLIKE, COMMENT_REPLIES, COMMENTS
} from '@/constants/endpoints.constant';
import {UserObservable} from '@/observables/user.observable';
import { FLAGS } from '@/constants/flags.constant';

import EmptyState from '@/assets/icons/no-comments.svg';
import LikeComment from '@/assets/icons/like-comment.svg';
import LikedComment from '@/assets/icons/liked-comment.svg';
import FlagComment from '@/assets/icons/flag-comment.svg';
import DeleteComment from '@/assets/icons/delete-comment.svg';
import EditComment from '@/assets/icons/edit-comment.svg';
import ReplyComment from '@/assets/icons/reply-comment.svg';
import FlaggedComment from '@/assets/icons/flagged-comment.svg';
import InfiniteScroll from 'react-infinite-scroll-component';

const UpdateActionDelete = 'delete';
const UpdateActionFlag = 'flag';
const UpdateActionEdit = 'edit';
const UpdateActionSetReplies = 'set_replies';
const UpdateActionAddReply = 'add_reply';

const ReplyLimit = 5;

const formatDate = (date) => {
  const now = new Date();
  const currentDate = new Date(date);

  const diff = Math.abs(now - currentDate);

  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) {
    return `${minutes}m`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h`;
  }


  const day = currentDate.getDate();
  const month = currentDate.toLocaleString('default', { month: 'short' });

  return `${month} ${day}`;
}

const prependComments = (prev_comments, new_comments) => {
  // Create a map of existing comments using uuid as key
  const existingCommentsMap = new Map(prev_comments.map(comment => [comment.uuid, comment]));

  // Filter new comments while preserving order
  const uniqueNewComments = new_comments.filter(comment => {
    if (!existingCommentsMap.has(comment.uuid)) {
      // Add comment to map to mark it as seen
      existingCommentsMap.set(comment.uuid, comment);
      return true; // Include the unique comment
    }
    return false;
  });

  // Prepend unique new comments to existing comments
  return uniqueNewComments.concat(prev_comments);
}

const appendComments = (prev_comments, new_comments) => {
  // Create a map of existing comments using uuid as key
  const existingCommentsMap = new Map(prev_comments.map(comment => [comment.uuid, comment]));

  // Filter new comments while preserving order
  const uniqueNewComments = new_comments.filter(comment => {
    if (!existingCommentsMap.has(comment.uuid)) {
      // Add comment to map to mark it as seen
      existingCommentsMap.set(comment.uuid, comment);
      return true; // Include the unique comment
    }
    return false;
  });

  // Append unique new comments to existing comments
  return prev_comments.concat(uniqueNewComments);
}

const RenderFlagComment = ({ articleId, comment, onCancel, onUpdate }) => {
  const { t } = useTranslation();
  const { execute: flagComment, loading } = useHttpPost();
  const [flagOptionSelected, setFlagOptionSelected] = useState();
  const [flagReason, setFlagReason] = useState('');

  const markComment = () => {
    flagComment(COMMENTS_FLAG(articleId, comment.uuid), {
      comment_flag_type: flagOptionSelected,
      comment_flag_reason: flagReason
    }).then((response) => {
      comment.flag = response;
      onUpdate(comment, UpdateActionFlag)
    })
  }

  return (
    <div className={styles.cgCommentsSection__fullSection}>
      <p className={styles.cgCommentsSection__header} >{t('moleculeTranslations.comments.flagComment')}</p>
      <ButtonGroup className={styles.cgCommentsSection__radioButtons}>
        {FLAGS.map((flag, idx) => (
          <RadioButtonComponent
            key={idx}
            id={`radio-${idx}`}
            type="radio"
            name="radio"
            value={flag.value}
            checked={flagOptionSelected === flag?.value}
            onChange={(e) => setFlagOptionSelected(e.currentTarget.value)}
            className={styles.cgCommentsSection__radioButton}
          >
            <span className={styles.cgCommentsSection__radioButton__label}>{flag.label}</span>
          </RadioButtonComponent>
        ))}

      </ButtonGroup>
        <textarea
          className={styles.cgCommentsSection__inputFlag}
          name='flag comment'
          id='flagComment'
          placeholder={t('moleculeTranslations.comments.flagPlaceholder')}
          value={flagReason}
          onChange={(e) => setFlagReason(e.target.value)}
        />
      <div className={styles.cgCommentsSection__buttonGroup}>
        <Button className={styles.cgCommentsSection__cancelButton} onClick={onCancel}>{t('moleculeTranslations.comments.cancel')}</Button>
        <Button className={styles.cgCommentsSection__postButton} onClick={markComment}>{loading ?
          < Spinner animation="border" role="status" /> :
          t('moleculeTranslations.comments.flag')
        }</Button>
      </div>
    </div>
  )
}

const RenderDeleteComment = ({ articleId, comment, onCancel, onUpdate }) => {
  const { t } = useTranslation();
  const { execute: deleteComment, loading } = useHttpDelete();

  const removeComment = () => {
    deleteComment(COMMENTS_DELETE(articleId, comment.uuid)).then(() => {
      onUpdate(comment, UpdateActionDelete);
    })
  }

  return (
    <div className={styles.cgCommentsSection__fullSection}>
      <p className={styles.cgCommentsSection__header} >{t('moleculeTranslations.comments.deleteHeader')}</p>
      <p className={styles.cgCommentsSection__deleteDescription}>{t('moleculeTranslations.comments.deleteDescription')}</p>
      <div className={styles.cgCommentsSection__buttonGroup}>
        <Button className={styles.cgCommentsSection__cancelButton} onClick={onCancel}>{t('moleculeTranslations.comments.cancel')}</Button>
        <Button className={styles.cgCommentsSection__postButton} onClick={removeComment}>
          {loading ?
            < Spinner animation="border" role="status" /> :
            t('moleculeTranslations.comments.delete')
          }
        </Button>
      </div>
    </div>
  )
};

const RenderComment = ({ articleId, comment, onDelete, onFlag, onUpdate }) => {
  const { t } = useTranslation();
  const [user] = useObservable(UserObservable.user$);
  const [isEdit, setIsEdit] = useState(false);
  const [commentUpdated, setCommentUpdated] = useState(comment?.comment_raw);
  const [newReply, setReply] = useState('');
  const [addReply, setAddReply] = useState(false);
  const [focused, setFocused] = useState(false);
  const [showReplies, setShowReplies] = useState(false)
  const [isLiked, setIsLiked] = useState(!!comment?.like)
  const [likeCount, setLikeCount] = useState(comment?.like_count || 0)
  const isFlagged = !!comment?.flag;
  const { data: replies, execute: getReplies } = useHttpGet();
  const { execute: addComment, loading: isLoadingReply } = useHttpPost();
  const { execute: likeComment } = useHttpPost();
  const { execute: unLikeComment } = useHttpDelete();
  const { data: editRest, execute: editComment, loading } = useHttpPut();

  useEffect(() => {
    if (editRest?.replies) {
    //   onUpdate(editRest?.replies);
      setCommentUpdated('');
      toggleEdit();
    }
  }, [editRest, onUpdate]);

  const toggleEdit = () => {
    setCommentUpdated(comment.comment_raw);
    setIsEdit(!isEdit);
  }

  const toggleReply = () => {
    if (!addReply) {
      if (comment.parent) {
        setReply(`@${comment?.author?.username} `);
      }
      setAddReply(true);
    } else {
      setReply('');
      setAddReply(false);
    }
  }

  const toggleReplies = () => {
    // if comment isn't a top level comment, don't allow showing replies, because they don't have replies to show
    if (comment.parent) {
      return
    }
    if (!showReplies) {
      setShowReplies(true);
      if (!replies) {
        // if no replies loaded yet, load them
        loadMoreReplies()
      }
    } else {
      setShowReplies(false)
    }
  }

  const loadMoreReplies = () => {
    const params = new URLSearchParams({});
    if ((replies && replies.pagination.page == replies.pagination.total_pages) || comment.replies?.length >= comment.reply_count) {
      return;
    }
    params.append('page', ((replies?.pagination?.page || 0) + 1).toString());
    params.append('limit', ReplyLimit.toString());
    getReplies(`${COMMENT_REPLIES(articleId, comment?.uuid)}?${params.toString()}`).then(response => {
      onUpdate(Object.assign({}, comment, { replies: appendComments(comment.replies || [], response.items || [])}), UpdateActionSetReplies);
    })
  }

  const saveComment = () => {
    editComment(COMMENTS_EDIT(articleId, comment?.uuid), {
      comment: commentUpdated
    }).then((result) => {
      onUpdate(result, UpdateActionEdit);
      setIsEdit(!isEdit);
    })
  }

  const onLike = (id) => {
    if (isLiked) {
      unLikeComment(COMMENTS_UNLIKE(articleId, id))
      setIsLiked(false)
      setLikeCount(likeCount - 1)
    } else {
      likeComment(COMMENTS_LIKE(articleId, id))
      setIsLiked(true)
      setLikeCount(likeCount + 1)
    }
  }

  const replyToComment = () => {
    const root = !comment?.root_parent_uuid ? comment?.uuid : comment?.root_parent_uuid
    const parent = comment?.uuid

    addComment(COMMENTS_CREATE(articleId), {
        parent_uuid: parent,
        root_parent_uuid: root,
        comment_raw: newReply,
    }).then((result) => {
      if (comment.parent) {
        result.parent = comment.parent;
      } else {
        result.parent = comment;
      }
      onUpdate(result, UpdateActionAddReply);
      setReply('');
      setAddReply(false);
      if (!showReplies) {
        toggleReplies();
      }
    })
  };

  const handleFocus = () => {
    setFocused(true);
  };

  const handleBlur = () => {
    setFocused(commentUpdated !== '' || newReply !== '');
  };

  return (<>
  <div className={styles.cgCommentsSection__comment}>
    <div className={styles.cgCommentsSection__comment__header}>
      <div className={styles.cgCommentsSection__comment__author}>
        <Avatar className={styles.cgCommentsSection__comment__avatar} name={comment?.author?.username}
                photo={comment?.author?.avatar?.location} lazyLoadAvatar={false}/>
        <p className={styles.cgCommentsSection__comment__username}>{comment?.author?.username}</p>
      </div>
      <p className={styles.cgCommentsSection__comment__date}>{formatDate(comment?.created_at)} </p>
    </div>
    {
      !isEdit ? <>
            <div dangerouslySetInnerHTML={{__html: comment?.comment}}/>
            <div className={styles.cgCommentsSection__comment__actions}>
              <div className={styles.cgCommentsSection__actionsGroup}>

                {
                  comment?.author?.username === user?.username ? <>
                        <button onClick={toggleEdit} className={styles.cgCommentsSection__comment__svgButtons}>
                          <EditComment/>
                        </button>
                        <button onClick={() => {
                          onDelete(comment)
                        }} className={styles.cgCommentsSection__comment__svgButtons}>
                          <DeleteComment/>
                        </button>
                      </> :
                      <>
                        {isLiked ?
                            <button className={styles.cgCommentsSection__comment__svgButtons} onClick={() => {
                              onLike(comment.uuid)
                            }}>
                              <LikedComment/>{!!likeCount &&
                                <p className={styles.cgCommentsSection__comment__likeText}> {likeCount}</p>}
                            </button> :
                            <button className={styles.cgCommentsSection__comment__svgButtons} onClick={() => {
                              onLike(comment.uuid)
                            }}>
                              <LikeComment/> {!!comment?.like_count &&
                                <p className={styles.cgCommentsSection__comment__likeText}> {comment?.like_count}</p>}
                            </button>
                        }
                        {isFlagged ?
                            <button className={styles.cgCommentsSection__comment__svgButtons}>
                              <FlaggedComment/>
                            </button> :
                            <button className={styles.cgCommentsSection__comment__svgButtons} onClick={() => {
                              onFlag(comment)
                            }}>
                              <FlagComment/>
                            </button>
                        }

                      </>
                }
                {comment?.reply_count > 0 &&
                    <div>
                      {!showReplies &&
                        <button
                          className={`${styles.cgCommentsSection__comment__svgButtons} ${styles.cgCommentsSection__comment__actions__reply}`}
                          onClick={toggleReplies}><CgIcon
                          iconName="HiOutlineChatBubbleOvalLeft"/> {t('moleculeTranslations.comments.view')} {comment.reply_count} {t('moleculeTranslations.comments.replies')}
                        </button>
                      }
                    </div>
                }
                {
                  showReplies &&
                  <button
                    className={`${styles.cgCommentsSection__comment__svgButtons} ${styles.cgCommentsSection__comment__actions__reply}`}
                    onClick={toggleReplies}><CgIcon
                    iconName="HiOutlineChatBubbleOvalLeft"/> {t('moleculeTranslations.comments.hide')} </button>
                }
              </div>
              <button
                className={`${styles.cgCommentsSection__comment__svgButtons} ${styles.cgCommentsSection__comment__actions__reply}`}
                onClick={toggleReply}>
                <ReplyComment className={styles.cgCommentsSection__comment__svgButtons}/>
                {t('moleculeTranslations.comments.reply')}
              </button>
            </div>
            {
              addReply &&
              <>
              <textarea
                className={`${styles.cgCommentsSection__input} ${focused ? styles.cgCommentsSection__input__resized : ''}`}
                name='comment_reply'
                id='comment_reply'
                value={newReply}
                rows={focused ? 3 : 1}
                placeholder={newReply}
                onChange={(e) => { setReply(e.target.value) }}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
                <div className={styles.cgCommentsSection__buttonGroup}>
                  <Button className={styles.cgCommentsSection__cancelButton} onClick={toggleReply}>{t('moleculeTranslations.comments.cancel')}</Button>
                  <Button className={styles.cgCommentsSection__postButton} onClick={replyToComment}>{isLoadingReply ?
                    < Spinner animation="border" role="status" /> :
                    t('moleculeTranslations.comments.post')
                  }</Button>
                </div>
              </>
            }
            {
                showReplies && comment?.replies && comment.replies.length &&
                <div className={styles.cgCommentsSection__comment__replies}>
                  {
                    comment.replies?.map((reply) => {
                      reply.parent = comment; // set parent on comment for later use, should be ok to override
                      return <RenderMemoComment
                        articleId={articleId} comment={reply} key={reply.uuid}
                        onDelete={onDelete} onFlag={onFlag} onUpdate={onUpdate} isReply={true}/>
                    })
                  }
                  { showReplies && <div className={styles.cgCommentsSection__comment__replies_bottom_actions}>
                    {(replies && replies.pagination.total_pages > replies.pagination.page) || (comment.replies?.length < comment.reply_count) ?
                      <button
                        className={`${styles.cgCommentsSection__comment__svgButtons} ${styles.cgCommentsSection__comment__actions__reply}`}
                        onClick={loadMoreReplies}><CgIcon
                        iconName="HiOutlineChatBubbleOvalLeft"/> {t('moleculeTranslations.comments.moreReplies')}</button>
                      :
                        comment?.replies?.length > 10 &&
                        <button
                            className={`${styles.cgCommentsSection__comment__svgButtons} ${styles.cgCommentsSection__comment__actions__reply}`}
                            onClick={toggleReplies}><CgIcon
                            iconName="HiOutlineChatBubbleOvalLeft"/> {t('moleculeTranslations.comments.hide')}
                        </button>
                    }
                  </div>
                  }
                </div>
            }
      </> :
        <div>
          <textarea
            className={`${styles.cgCommentsSection__input} ${focused ? styles.cgCommentsSection__input__resized : ''}`}
            name='edit comment'
            id='editComment'
            value={commentUpdated}
            rows={focused ? 3 : 1}
            onChange={(e) => { setCommentUpdated(e.target.value) }}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
            <div className={styles.cgCommentsSection__buttonGroup}>
              <Button className={styles.cgCommentsSection__cancelButton}
                      onClick={toggleEdit}>{t('moleculeTranslations.comments.cancel')}</Button>
              <Button className={styles.cgCommentsSection__postButton} onClick={saveComment}>
                {loading ?
                    < Spinner animation="border" role="status"/> :
                    t('moleculeTranslations.comments.update')
                }
              </Button>
            </div>
          </div>
    }
  </div>
  </>)
}

const RenderMemoComment = memo(RenderComment, (prevProps, nextProps) => {
  const prevComment = prevProps.comment;
  const nextComment = nextProps.comment;
  if (prevComment.uuid !== nextComment.uuid || prevComment.comment !== nextComment.comment || prevComment.comment_raw !== nextComment.comment_raw) {
    return false;
  }
  if (prevComment.replies?.length !== nextComment.replies?.length) {
    return false;
  }
  return true;
});

const CommentSection = ({ articleId, articleTitle, isOpen, onClose, showLogin, isLoggedIn, commentCount }) => {
  const { t } = useTranslation();
  const [showDeleteComment, setShowDeleteComment] = useState(null);
  const [showFlagComment, setShowFlagComment] = useState(null);
  const [currentComments, setCurrentComments] = useState([]);
  const { execute: getThreads, data: thread } = useHttpGet();
  const { execute: addComment, loading } = useHttpPost();
  const [focused, setFocused] = useState(false);
  const [sort, setSort] = useState('desc');
  const [newComment, setNewComment] = useState('');

  useEffect(() => {
    if (isOpen) {
      const params = new URLSearchParams({});
      params.append('sort', sort);
      setCurrentComments([]);
      getThreads(`${COMMENTS(articleId)}?${params.toString()}`).then(thread => {
        setCurrentComments(thread.comments);
      });
    }
  }, [articleId, isOpen, sort])

  const onUpdate = (updatedComment, action) => {
    // ensure that right panel is reset
    setShowDeleteComment(null);
    setShowFlagComment(null);

    let parent;
    const payload = {};
    let updateCommentUuid = updatedComment.uuid;
    switch (action) {
      case UpdateActionDelete:
        if (updatedComment.parent) {
          parent = updatedComment.parent;
          updateCommentUuid = parent.uuid;
          payload.replies = parent.replies.filter(comment => comment.uuid !== updatedComment.uuid);
          payload.reply_count = parent.reply_count - 1;
        } else {
          setCurrentComments(currentComments.filter(comment => comment.uuid !== updatedComment.uuid));
          return; // nothing left to do
        }
        break;
      case UpdateActionAddReply:
        parent = updatedComment.parent;
        updateCommentUuid = parent.uuid;
        payload.replies = prependComments(parent.replies || [], [updatedComment]);
        payload.reply_count = parent.reply_count + 1;
        break;
      case UpdateActionSetReplies:
        payload.replies = updatedComment.replies;
        break;
      case UpdateActionFlag:
        payload.flag = updatedComment.flag;
        break;
      case UpdateActionEdit:
        payload.comment = updatedComment.comment;
        payload.comment_raw = updatedComment.comment_raw;
        break;
      default:
        return; // unknown action, do nothing
    }
    setCurrentComments(currentComments.map(function(comment) {
      if (comment.uuid === updateCommentUuid) {
        return Object.assign({}, comment, payload);
      } else {
        return comment;
      }
    }));
  }

  const confirmDelete = (comment) => {
    setShowDeleteComment(comment);
    setShowFlagComment(null); // shouldn't happen, but just make sure
  }

  const confirmFlag = (comment) => {
    setShowFlagComment(comment);
    setShowDeleteComment(null); // shouldn't happen, but just make sure
  }

  const cancelAction = () => {
    setShowDeleteComment(null);
    setShowFlagComment(null);
  }

  const handleSortChange = (newSort) => {
    setSort(newSort);
  }

  const getMoreComments = () => {
    const params = new URLSearchParams({});
    params.append('page', (thread?.comments_pagination?.page) +1);
    params.append('sort', sort);
    getThreads(`${COMMENTS(articleId)}?${params.toString()}`).then(thread => {
      setCurrentComments(prevComments => appendComments(prevComments || [], thread.comments || []))
    });
  }

  const renderComments = () => {
    if (currentComments?.length === 0) {
      return (
          <div className={styles.cgCommentsSection__noComments}>
            <EmptyState className={styles.cgCommentsSection__noComments__image}/>
            <p className={styles.cgCommentsSection__noComments__text}>{t('moleculeTranslations.comments.noComments')}</p>
          </div>
      )
    } else {
      return (
          <div>
            <InfiniteScroll
                next={getMoreComments}
                loader={<div className="w-100 text-center"><div className="paginated-loader lds-dual-ring"></div></div>}
                dataLength={commentCount}
                hasMore={thread?.comments_pagination?.page < thread?.comments_pagination?.total_pages}
                className={styles.cgCommentsSection__infiniteScroll}
            >
              { currentComments?.map((comment, index) => {
                return (
                    !comment?.root_parent_uuid && <div key={index} className={styles.cgCommentsSection__comments}>
                      {
                        <RenderMemoComment
                            articleId={articleId} comment={comment} key={index}
                            onDelete={confirmDelete} onFlag={confirmFlag} onUpdate={onUpdate}/>
                      }
                    </div>
                );
              })}
            </InfiniteScroll>
          </div>
      )
    }
  }

  const postComment = async () => {
    addComment(COMMENTS_CREATE(articleId), { comment_raw: newComment }).then((result) => {
      setCurrentComments(prependComments(currentComments || [], [result]));
    })
    setNewComment('');
  };

  const handleFocus = () => {
    setFocused(true);
  };

  const handleBlur = () => {
    setFocused(newComment !== '');
  };

  const clickComments = () => {
    if (!isLoggedIn) {
      onClose();
      showLogin();
    }
  }

  const renderPanelContent = () => {
    if (showDeleteComment !== null) {
      return <RenderDeleteComment articleId={articleId} comment={showDeleteComment} onCancel={cancelAction} onUpdate={onUpdate} />
    }
    if (showFlagComment !== null) {
      return <RenderFlagComment articleId={articleId} comment={showFlagComment} onCancel={cancelAction} onUpdate={onUpdate} />
    }

    return (
      <div className={`${styles.cgCommentsSection}`} onClick={clickComments}>
        <p className={styles.cgCommentsSection__heading}> {t('moleculeTranslations.comments.responesesTo')} </p>
        <p className={styles.cgCommentsSection__articleTitle}>{articleTitle}</p>
        <textarea
          className={`${styles.cgCommentsSection__input} ${focused ? styles.cgCommentsSection__input__resized : ''}`}
          name='comment'
          id='comment'
          rows={focused ? 3 : 1}
          placeholder={focused ? '' : t('moleculeTranslations.comments.addComment')}
          value={newComment}
          onChange={(e) => {
            setNewComment(e.target.value)
          }}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />

        <div className={styles.cgCommentsSection__buttonGroup}>
          <Button className={styles.cgCommentsSection__cancelButton}
                  onClick={onClose}>{t('moleculeTranslations.comments.cancel')}</Button>
          <Button className={styles.cgCommentsSection__postButton} onClick={postComment} disabled={!newComment}>
            {loading ?
              <Spinner animation="border" role="status" /> :
              t('moleculeTranslations.comments.post')
            }
          </Button>
        </div>
        <div className={styles.cgCommentsSection__sortOptions}>
          {sort === 'asc' ?
            <div className={styles.cgCommentsSection__sortOptions__options} onClick={() => handleSortChange('desc')}>
              <span>Oldest </span> <CgIcon iconName="BiChevronDown"/>
            </div>
            :
            <div className={styles.cgCommentsSection__sortOptions__options} onClick={() => handleSortChange('asc')}>
              <span>Most Recent </span><CgIcon iconName="BiChevronUp"/>
            </div>
          }
        </div>
        {renderComments()}
      </div>
    );
  }

  return (
    <RightPanel isOpen={isOpen} onClose={onClose}  panelClass={styles.cgCommentsSection__rightPanel} containerClass={styles.cgCommentsSection__rightPanelContainer}>
      { renderPanelContent() }

    </RightPanel >)
}

RenderFlagComment.propTypes = {
  articleId: PropTypes.string,
  comment: PropTypes.object,
  onCancel: PropTypes.func,
  onUpdate: PropTypes.func,
}

RenderDeleteComment.propTypes = {
  articleId: PropTypes.string,
  comment: PropTypes.object,
  onCancel: PropTypes.func,
  onUpdate: PropTypes.func,
}

RenderComment.propTypes = {
  articleId: PropTypes.string,
  comment: PropTypes.object,
  onDelete: PropTypes.func,
  onFlag: PropTypes.func,
  onUpdate: PropTypes.func,
  isReply: PropTypes.bool
}

CommentSection.propTypes = {
  articleId: PropTypes.string,
  comments: PropTypes.array,
  articleTitle: PropTypes.string,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  showLogin: PropTypes.func,
  isLoggedIn: PropTypes.bool,
  commentCount: PropTypes.number,
}
export default CommentSection;
